import { OnDestroy, HostListener } from '@angular/core';
import { Observable, BehaviorSubject, ReplaySubject } from 'rxjs';
import { HttpEventType, HttpProgressEvent, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { UploadStorage } from '../../storages/upload-storage';
import { filter, tap, first, map, takeUntil } from 'rxjs/operators';
import { deserialize } from 'santee-dcts';
import { UploadedFilesResponse, UploadedFileResponse, isUploadedFilesResponse, isDocumentFolderMissingResponse, isInvalidDocumentMetadataResponse, InvalidDocumentMetadataResponse, UploadErrorDescription } from '../../../../covance-file-upload/uploaded-file-response';
import { CustomMethodUploadOptions, MethodUploadOptions } from './method-upload-options';
import { FormGroup } from '@angular/forms';
import { MdlDialogReference } from '@angular-mdl/core';

export interface UploadProgress {
    loaded: number;
    total: number;
}

export enum UploadState {
    Ready,
    inProgress,
    InvalidFiles,
    InvalidMetadata,
    MissingDocumentFolder,
    ErrorUpload,
    SuccessUpload
}

/**
 * Implementing interface requires public modificator. 
 * ngOnDestory can be defined without 'implements OnDestroy' keyword, because of duck-typing. Other hand, we should depend on OnDestroy definintion.
 */
const ngOnDestroyMethod: keyof OnDestroy = 'ngOnDestroy';

export abstract class UploadDialogBase {
    public readonly editorForm = new FormGroup({});

    protected confirmationDialogOpened = false;
    protected errorDetails$: Observable<string[]>;
    protected get onDialogDestroying(): Observable<void> {
        return this.onDialogDestroying$;
    }

    private readonly uploadState$ = new BehaviorSubject<UploadState>(UploadState.Ready);
    private readonly uploadResult$ = new BehaviorSubject<UploadedFileResponse[]>(null);
    private readonly uploadErrorDescription$ = new BehaviorSubject<UploadErrorDescription>(null);
    private readonly uploadProgress$ = new BehaviorSubject<UploadProgress>(null);
    private readonly errorMessage$: Observable<string>;

    // Using ReplaySubject is to handle the case when someone subscribed after onDialogDestroying$ had been completed
    private readonly onDialogDestroying$ = new ReplaySubject<void>(1);

    public get uploadErrors(): Observable<UploadedFileResponse[] | null> {
        return this.uploadResult$.pipe(
            map(x => x ? x.filter(fileReponse => !fileReponse.isValid) : null)
        );
    }

    public get uploadProgress(): Observable<UploadProgress | null> {
        return this.uploadProgress$;
    }

    public get uploadState(): Observable<UploadState> {
        return this.uploadState$;
    }

    public get disableForm(): Observable<boolean> {
        return this.uploadState$.pipe(map(state => state === UploadState.inProgress || state === UploadState.SuccessUpload));
    }

    public get inProgress(): boolean {
        return this.uploadState$.value === UploadState.inProgress;
    }

    public get errorMessage(): Observable<string> {
        return this.errorMessage$;
    }

    protected constructor(
        private readonly uploadStorage: UploadStorage,
        protected readonly dialog: MdlDialogReference,
    ) {
        this.errorMessage$ = this.uploadErrorDescription$.pipe(filter(d => !!d), map(d => d.message));
        this.errorDetails$ = this.uploadErrorDescription$.pipe(filter(d => !!d), map(d => d.details));
        this.disableEditorForm();
    }

    public upload(options: MethodUploadOptions): Promise<void>;
    public upload(options: CustomMethodUploadOptions): Promise<void>;
    public async upload(options: unknown): Promise<void> {
        this.uploadResult$.next(null);
        this.uploadProgress$.next(null);

        let request: Observable<HttpEvent<Object>>;
        if (options instanceof MethodUploadOptions) {
            request = this.uploadStorage.create(options.categoryId, options.files, options.documentMetadata);
        } else if (options instanceof CustomMethodUploadOptions) {
            request = options.customUploadMethod();
        } else {
            throw new Error('Invalid options');
        }

        try {
            await request
                .pipe(
                    tap(this.handleHttpEvent),
                    filter(event => event.type === HttpEventType.Response),
                    tap(options.onSuccessfulUpload),
                    first()
                ).toPromise();
        } catch (e) {
            this.handleError(e);
        }
    }

    @HostListener('document:keydown.esc')
    public close() {
        if (!this.confirmationDialogOpened && !this.inProgress && this.dialog) {
            this.dialog.hide();
        }
    }

    private handleHttpEvent = (event: HttpEvent<Object>) => {
        switch (event.type) {
            case HttpEventType.UploadProgress:
                this.handleProgress(event);
                break;
            case HttpEventType.Response:
                this.handleOkResponse(event);
                break;
        }
    }

    private handleProgress(event: HttpProgressEvent) {
        this.uploadProgress$.next({ total: event.total, loaded: event.loaded });
        this.uploadState$.next(UploadState.inProgress);
    }

    private handleOkResponse(event: HttpResponse<Object>) {
        if (event.ok) {
            this.uploadResult$.next(null);
            this.uploadState$.next(UploadState.SuccessUpload);
        }
    }

    private handleError(e: unknown) {
        if (e instanceof HttpErrorResponse) {
            if (e.status === 400 && isUploadedFilesResponse(e.error)) {
                const uploadedFileReponses = deserialize(e.error, UploadedFilesResponse).result;
                this.uploadResult$.next(uploadedFileReponses);
                this.uploadState$.next(UploadState.InvalidFiles);
                return;
            }
            if (e.status === 400 && isInvalidDocumentMetadataResponse(e.error)) {
                const invalidMetadataResponse = deserialize(e.error, InvalidDocumentMetadataResponse).result;
                this.uploadErrorDescription$.next(invalidMetadataResponse);
                this.uploadState$.next(UploadState.InvalidMetadata);
                return;
            }
            if (e.status === 404 && isDocumentFolderMissingResponse(e.error)) {
                this.uploadState$.next(UploadState.MissingDocumentFolder);
                return;
            }
        }

        this.uploadState$.next(UploadState.ErrorUpload);
        throw e;
    }

    private disableEditorForm() {
        this.disableForm.pipe(
            takeUntil(this.onDialogDestroying)
        ).subscribe(isDisabled => {
            if (isDisabled) {
                // ({ emitEvent: false }) in order to fix Angular bug related to valueChanges -> disable/enable emits valueChanges
                this.editorForm.disable(({ emitEvent: false }));
            } else {
                this.editorForm.enable(({ emitEvent: false }));
            }
        });
    }

    /**
     * Don't define ngOnDestroy in derived classes. Use {@link UploadDialogBase#onDialogDestroying} instead.
    Using:
    ```ts
    .pipe(
        // ... operators
        takeUntil(this.onDialogDestroying)
    ).subscribe(...)
    ```
    or
    ```ts
    .subscribe(() => {
        // free others resources
    })
    ```
     * Private modificator prevents the ngOnDestroy definiting in derived classes to avoid hiding the base class ngOnDestroy.
     * Called by angular
     */
    private [ngOnDestroyMethod]() {
        this.uploadResult$.complete();
        this.uploadErrorDescription$.complete();
        this.uploadProgress$.complete();
        this.uploadState$.complete();
        this.onDialogDestroying$.next();
        this.onDialogDestroying$.complete();
    }
}